/**
 * Propiedades del elemento elegido: posición y tamaño en mm, páginas, capa,
 * contenido, estilo, condición y, en las tablas, sus columnas.
 */
import { useState } from 'react'
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ds/Button'
import { CheckboxField } from '@/components/ds/CheckboxField'
import { IconButton } from '@/components/ds/IconButton'
import { SelectField } from '@/components/ds/SelectField'
import { TextField } from '@/components/ds/TextField'
import type { DatosDelElemento } from '@/modules/config/hooks/usePrintTemplates'
import { SelectorDeArchivo } from './Biblioteca'
import {
  altoDeFila, conAltoDeLista, type CampoDeLista, type Columna, type Definicion, type Elemento, type Estilo,
  type FilaDeLista,
} from './geometria'

const NOMBRE_TIPO: Record<string, string> = {
  text: 'Texto', field: 'Campo', field_list: 'Lista de datos', rect: 'Recuadro', line: 'Línea',
  detail_table: 'Tabla de detalle', references_table: 'Referencias', timbre: 'Timbre', qr: 'Código QR',
  image: 'Imagen', barcode: 'Código de barras',
}

const FORMATOS = [
  { value: '', label: 'Sin formato' },
  { value: 'money', label: 'Monto (1.234)' },
  { value: 'number:2', label: 'Número con 2 decimales' },
  { value: 'quantity', label: 'Cantidad (sin ceros de más)' },
  { value: 'rut', label: 'RUT (11.111.111-1)' },
  { value: 'date', label: 'Fecha (DD-MM-AAAA)' },
  { value: 'date_long', label: 'Fecha larga (19 de octubre de 2026)' },
  { value: 'year', label: 'Año (2026)' },
  { value: 'payment_method', label: 'Forma de pago (Contado, Crédito)' },
  { value: 'transfer_type', label: 'Tipo de traslado de la guía' },
  { value: 'amount_in_words', label: 'Monto en palabras' },
  { value: 'doc_type', label: 'Nombre del tipo de DTE' },
  { value: 'upper', label: 'Mayúsculas' },
]

function Numero({ label, value, onChange, paso = 1 }: {
  label: string; value: number | undefined; onChange: (v: number) => void; paso?: number
}) {
  return (
    <TextField dense label={label} value={value ?? ''}
      inputProps={{ type: 'number', step: paso, inputMode: 'decimal' }}
      onChange={e => { const v = Number(e.target.value); if (e.target.value !== '' && Number.isFinite(v)) onChange(v) }} />
  )
}

function EditorDeEstilo({ estilo, onChange, conTexto = true }: {
  estilo: Estilo; onChange: (e: Estilo) => void; conTexto?: boolean
}) {
  const fijar = (cambio: Partial<Estilo>) => onChange({ ...estilo, ...cambio })
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">Estilo</legend>
      {conTexto && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <SelectField dense label="Fuente" value={estilo.font_family ?? ''}
              onChange={e => fijar({ font_family: (e.target.value || undefined) as Estilo['font_family'] })}>
              <option value="">La de la plantilla</option>
              <option value="sans">Sans (Arial)</option>
              <option value="serif">Serif (Times)</option>
              <option value="mono">Mono (Courier)</option>
            </SelectField>
            <Numero label="Tamaño (pt)" value={estilo.font_size_pt} paso={0.5} onChange={v => fijar({ font_size_pt: v })} />
          </div>
          <div className="flex flex-wrap gap-3">
            <CheckboxField label="Negrita" checked={!!estilo.bold} onChange={e => fijar({ bold: e.target.checked })} />
            <CheckboxField label="Cursiva" checked={!!estilo.italic} onChange={e => fijar({ italic: e.target.checked })} />
            <CheckboxField label="Mayúsculas" checked={!!estilo.uppercase} onChange={e => fijar({ uppercase: e.target.checked })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SelectField dense label="Alineación" value={estilo.align ?? 'left'} onChange={e => fijar({ align: e.target.value as Estilo['align'] })}>
              <option value="left">Izquierda</option><option value="center">Centro</option><option value="right">Derecha</option>
            </SelectField>
            <SelectField dense label="Vertical" value={estilo.valign ?? 'top'} onChange={e => fijar({ valign: e.target.value as Estilo['valign'] })}>
              <option value="top">Arriba</option><option value="middle">Al medio</option><option value="bottom">Abajo</option>
            </SelectField>
          </div>
        </>
      )}
      <div className="space-y-1.5">
        {conTexto && <Color label="Texto" value={estilo.color} onChange={v => fijar({ color: v })} />}
        <Color label="Fondo" value={estilo.background} vacio onChange={v => fijar({ background: v })} />
        <Color label="Borde" value={estilo.border_color} onChange={v => fijar({ border_color: v })} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Numero label="Borde (mm)" value={estilo.border_mm} paso={0.1} onChange={v => fijar({ border_mm: v })} />
        <Numero label="Esquinas (mm)" value={estilo.radius_mm} paso={0.5} onChange={v => fijar({ radius_mm: v })} />
        <Numero label="Relleno (mm)" value={estilo.padding_mm} paso={0.5} onChange={v => fijar({ padding_mm: v })} />
      </div>
    </fieldset>
  )
}

const HEX = /^#[0-9a-fA-F]{6}$/

/**
 * Un color: la muestra abre el selector del navegador y al lado va el código
 * `#RRGGBB`, para escribirlo o pegarlo (los colores de la marca).
 *
 * ⚠️ Sin color, la muestra se ve cuadriculada y no negra: el selector nativo
 * no avisa un cambio si se elige el mismo valor que ya tenía, así que con
 * negro de partida, elegir negro no hacía nada (el recuadro quedaba sin fondo).
 */
function Color({ label, value, onChange, vacio }: {
  label: string; value?: string; onChange: (v: string) => void; vacio?: boolean
}) {
  const [texto, setTexto] = useState(value ?? '')
  const [anterior, setAnterior] = useState(value)
  if (anterior !== value) {
    setAnterior(value)
    setTexto(value ?? '')
  }
  // El texto y el borde sin color elegido son negros; solo el fondo puede no tener color.
  const efectivo = value || (vacio ? '' : '#000000')
  const muestra = efectivo
    ? { background: efectivo }
    : { backgroundImage: 'repeating-conic-gradient(#cccccc 0 25%, #ffffff 0 50%)', backgroundSize: '8px 8px' }
  return (
    <div className="flex items-center gap-2 text-xs text-content-muted">
      <span className="w-12 shrink-0">{label}</span>
      <span className="relative h-7 w-9 shrink-0 rounded border border-divider overflow-hidden" style={muestra}>
        <input type="color" value={efectivo || '#808080'} onChange={e => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label={`Color de ${label.toLowerCase()}`} />
      </span>
      <input type="text" value={texto} maxLength={7} spellCheck={false} placeholder={vacio ? 'Sin color' : '#000000'}
        aria-label={`Código del color de ${label.toLowerCase()}`} className="cl-input cl-input--sm w-24 font-mono"
        onChange={e => {
          const v = e.target.value.trim()
          setTexto(v)
          if (HEX.test(v)) onChange(v.toLowerCase())
          else if (v === '' && vacio) onChange('')
        }} />
      {vacio && value && <button type="button" className="text-[11px] text-primary" onClick={() => onChange('')}>Quitar</button>}
    </div>
  )
}

const OTRA_RUTA = '__otra__'
const TEXTO_COMPUESTO = '__texto__'

/**
 * El campo de la columna, elegido de la lista que sale del XSD. «Otra ruta…»
 * deja escribirla a mano para lo que la lista no tenga, y «Texto con varios
 * campos…» compone la celda: el nombre del producto y su descripción debajo.
 */
function CampoDeColumna({ columna, campos, onChange }: {
  columna: Columna; campos: CampoDeLista[]; onChange: (cambio: Partial<Columna>) => void
}) {
  const ruta = columna.path ?? ''
  const compuesto = columna.text !== undefined
  const conocido = campos.some(c => c.ruta === ruta)
  const [aMano, setAMano] = useState(!conocido && ruta !== '')
  const valor = compuesto ? TEXTO_COMPUESTO : aMano || !conocido ? OTRA_RUTA : ruta
  return (
    <div className="space-y-1">
      <SelectField dense label="Campo" value={valor}
        title={campos.find(c => c.ruta === ruta)?.descripcion}
        onChange={e => {
          const elegido = e.target.value
          if (elegido === TEXTO_COMPUESTO) {
            // Parte del campo que ya tenía y se le suma lo demás.
            onChange({ text: ruta ? `{${ruta}}` : '', path: undefined })
            return
          }
          if (elegido === OTRA_RUTA) { setAMano(true); onChange({ text: undefined, path: ruta }); return }
          setAMano(false)
          onChange({ path: elegido, text: undefined })
        }}>
        {campos.map(c => <option key={c.ruta} value={c.ruta}>{c.nombre}</option>)}
        <option value={OTRA_RUTA}>Otra ruta…</option>
        <option value={TEXTO_COMPUESTO}>Texto con varios campos…</option>
      </SelectField>
      {compuesto ? (
        <TextField dense multiline rows={2} label="Texto de la columna" value={columna.text ?? ''}
          onChange={e => onChange({ text: e.target.value })} inputProps={{ spellCheck: false }}
          hint="Campos de la línea entre llaves; con un salto de línea, uno debajo del otro: {NmbItem} y {DscItem}." />
      ) : (aMano || !conocido) && (
        <TextField dense label="Ruta (relativa a la fila)" value={ruta} onChange={e => onChange({ path: e.target.value })}
          inputProps={{ spellCheck: false }}
          hint="Un campo de la línea que manda el sistema de origen: personalizado:DetPersonNUD_02." />
      )}
    </div>
  )
}

/**
 * Las columnas de una tabla. Una columna con condición va solo en los documentos
 * que la cumplen (la NC muestra el precio y la factura el neto, en el mismo
 * lugar): por eso la suma se separa en fijas y con condición, y el aviso mira
 * solo las fijas. `visibles`: cuáles salen con el documento de ejemplo.
 */
function EditorDeColumnas({ columnas, campos, onChange, visibles, titulo = 'Columnas', pista = true }: {
  columnas: Columna[]; campos: CampoDeLista[]; onChange: (c: Columna[]) => void; visibles?: boolean[]
  titulo?: string; pista?: boolean
}) {
  const fijas = columnas.filter(c => !c.condition).reduce((s, c) => s + (c.width_pct || 0), 0)
  const conCondicion = columnas.filter(c => c.condition).reduce((s, c) => s + (c.width_pct || 0), 0)
  const fuera = conCondicion > 0 ? fijas > 100.5 : Math.abs(fijas - 100) > 0.5
  const fijar = (i: number, cambio: Partial<Columna>) => onChange(columnas.map((c, j) => (j === i ? { ...c, ...cambio } : c)))
  const mover = (i: number, d: number) => {
    const copia = [...columnas]
    ;[copia[i], copia[i + d]] = [copia[i + d], copia[i]]
    onChange(copia)
  }
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">{titulo}</legend>
      {columnas.map((c, i) => (
        <div key={i} className="p-2 rounded-lg border border-divider space-y-1.5">
          {c.condition && (
            <p className="text-[11px] text-content-muted">
              <span className="px-1.5 py-0.5 rounded bg-surface-muted">Con condición</span>
              {visibles?.[i] === false && ' · No sale con el documento de ejemplo'}
            </p>
          )}
          <div className="flex items-center gap-1">
            <TextField dense label="Encabezado" value={c.header} onChange={e => fijar(i, { header: e.target.value })} className="flex-1" />
            <IconButton label="Subir" icon={<ArrowUp size={13} />} bare disabled={i === 0} onClick={() => mover(i, -1)} />
            <IconButton label="Bajar" icon={<ArrowDown size={13} />} bare disabled={i === columnas.length - 1} onClick={() => mover(i, 1)} />
            <IconButton label="Quitar columna" icon={<Trash2 size={13} />} bare disabled={columnas.length === 1}
              onClick={() => onChange(columnas.filter((_, j) => j !== i))} />
          </div>
          <CampoDeColumna columna={c} campos={campos} onChange={cambio => fijar(i, cambio)} />
          <div className="grid grid-cols-3 gap-1.5">
            <Numero label="Ancho %" value={c.width_pct} onChange={v => fijar(i, { width_pct: v })} />
            <SelectField dense label="Alinear" value={c.align ?? 'left'} onChange={e => fijar(i, { align: e.target.value as Columna['align'] })}>
              <option value="left">Izq.</option><option value="center">Centro</option><option value="right">Der.</option>
            </SelectField>
            <SelectField dense label="Formato" value={c.formats?.[0] ?? ''}
              onChange={e => fijar(i, { formats: e.target.value ? [e.target.value] : [] })}>
              {FORMATOS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </SelectField>
          </div>
          <details className="text-xs" open={!!c.condition || undefined}>
            <summary className="cursor-pointer text-content-muted">Condición (avanzado)</summary>
            <div className="mt-2">
              <TextField dense label="Se imprime solo si" value={c.condition ?? ''} placeholder="tipo == 61"
                onChange={e => fijar(i, { condition: e.target.value || undefined })}
                hint="Se evalúa sobre el documento. Dos columnas con condiciones opuestas (tipo == 61 · tipo != 61) y el mismo ancho ocupan el mismo lugar; si una no sale, las siguientes se corren y la tabla se angosta."
                inputProps={{ spellCheck: false }} />
            </div>
          </details>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" icon={<Plus size={13} />}
          onClick={() => {
            const primero = campos[0]?.ruta ?? 'NmbItem'
            onChange([...columnas, { header: campos[0]?.nombre.split(' › ').pop() ?? 'Columna', path: primero, width_pct: 15 }])
          }}>
          Agregar columna
        </Button>
        <span className={`text-[11px] ${fuera ? 'text-warning-content' : 'text-content-subtle'}`}>
          Suma de anchos: {fijas}%{conCondicion > 0 && ` + ${conCondicion}% con condición`}
        </span>
      </div>
      {pista && <p className="text-[11px] text-content-subtle">También se agrega una columna soltando un campo de la paleta sobre la tabla.</p>}
    </fieldset>
  )
}

const REFERENCIAS_DE_PARTIDA: Columna[] = [
  { header: 'Tipo documento', path: 'TpoDocRef', width_pct: 35, formats: ['doc_type'] },
  { header: 'Folio', path: 'FolioRef', width_pct: 15, align: 'right' },
  { header: 'Fecha', path: 'FchRef', width_pct: 15, formats: ['date'] },
  { header: 'Razón', path: 'RazonRef', width_pct: 35 },
]

/** Grilla y referencias de la tabla del detalle: lo que se ve en una factura impresa clásica. */
function OpcionesDelDetalle({ el, camposDeReferencias, fijar }: {
  el: Elemento; camposDeReferencias: CampoDeLista[]; fijar: (c: Partial<Elemento>) => void
}) {
  const grilla = el.grid || null
  const refs = el.references
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">Tabla</legend>
      <CheckboxField label="Líneas entre columnas" checked={!!grilla}
        description="Bajan hasta el final de la zona del detalle, aunque haya pocas líneas."
        onChange={e => fijar({ grid: e.target.checked ? { width_mm: 0.2 } : undefined })} />
      {grilla && (
        <Numero label="Grosor de las líneas (mm)" value={grilla.width_mm ?? 0.2} paso={0.05}
          onChange={v => fijar({ grid: { ...grilla, width_mm: v } })} />
      )}
      <CheckboxField label="Referencias sobre el detalle" checked={!!refs}
        description="Los documentos referenciados (la factura que corrige una nota de crédito, la orden de compra). Se imprimen solo si el documento los trae."
        onChange={e => fijar({ references: e.target.checked
          ? { title: 'Documentos referenciados', columns: REFERENCIAS_DE_PARTIDA } : undefined })} />
      {refs && (
        <div className="space-y-2 pl-2 border-l-2 border-divider">
          <TextField dense label="Título" value={refs.title ?? ''} onChange={e => fijar({ references: { ...refs, title: e.target.value } })} />
          <EditorDeColumnas titulo="Columnas de las referencias" pista={false} columnas={refs.columns}
            campos={camposDeReferencias} onChange={columns => fijar({ references: { ...refs, columns } })} />
        </div>
      )}
    </fieldset>
  )
}

/** Tamaño, negrita y alineación: lo que se ajusta por separado a rótulos y valores. */
function EstiloCorto({ titulo, estilo, onChange }: { titulo: string; estilo: Estilo; onChange: (e: Estilo) => void }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium text-content-muted">{titulo}</p>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5 items-end">
        <Numero label="Tamaño (pt)" value={estilo.font_size_pt} paso={0.5} onChange={v => onChange({ ...estilo, font_size_pt: v })} />
        <SelectField dense label="Alinear" value={estilo.align ?? 'left'} onChange={e => onChange({ ...estilo, align: e.target.value as Estilo['align'] })}>
          <option value="left">Izq.</option><option value="center">Centro</option><option value="right">Der.</option>
        </SelectField>
        <CheckboxField label="Negrita" checked={!!estilo.bold} onChange={e => onChange({ ...estilo, bold: e.target.checked })} />
      </div>
    </div>
  )
}

/**
 * Las filas de la lista de datos. El valor lleva campos entre llaves y, si
 * queda vacío con el documento, la fila no se imprime y las de abajo suben.
 */
function EditorDeLista({ el, fijar }: { el: Elemento; fijar: (c: Partial<Elemento>) => void }) {
  const filas = el.rows ?? []
  const ocultarPorDefecto = el.hide_empty !== false
  const conFilas = (rows: FilaDeLista[]) => fijar(conAltoDeLista({ ...el, rows }))
  const fijarFila = (i: number, cambio: Partial<FilaDeLista>) => conFilas(filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)))
  const mover = (i: number, d: number) => {
    const copia = [...filas]
    ;[copia[i], copia[i + d]] = [copia[i + d], copia[i]]
    conFilas(copia)
  }
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">Filas</legend>
      {filas.map((f, i) => (
        <div key={i} className="p-2 rounded-lg border border-divider space-y-1.5">
          <div className="flex items-center gap-1">
            <TextField dense label="Rótulo" value={f.label} onChange={e => fijarFila(i, { label: e.target.value })} className="flex-1" />
            <IconButton label="Subir" icon={<ArrowUp size={13} />} bare disabled={i === 0} onClick={() => mover(i, -1)} />
            <IconButton label="Bajar" icon={<ArrowDown size={13} />} bare disabled={i === filas.length - 1} onClick={() => mover(i, 1)} />
            <IconButton label="Quitar fila" icon={<Trash2 size={13} />} bare disabled={filas.length === 1}
              onClick={() => conFilas(filas.filter((_, j) => j !== i))} />
          </div>
          <TextField dense label="Valor" value={f.value} onChange={e => fijarFila(i, { value: e.target.value })}
            inputProps={{ spellCheck: false }} />
          <CheckboxField label="Ocultar si viene vacío" checked={f.hide_empty ?? ocultarPorDefecto}
            onChange={e => fijarFila(i, { hide_empty: e.target.checked === ocultarPorDefecto ? undefined : e.target.checked })} />
        </div>
      ))}
      <Button size="sm" variant="ghost" icon={<Plus size={13} />}
        onClick={() => conFilas([...filas, { label: 'Rótulo:', value: '' }])}>
        Agregar fila
      </Button>
      <p className="text-[11px] text-content-subtle">Soltar un campo de la paleta sobre la lista agrega una fila con ese campo.</p>
      <div className="grid grid-cols-2 gap-2">
        <Numero label="Ancho del rótulo (mm)" value={el.label_w_mm} paso={0.5} onChange={v => fijar({ label_w_mm: v })} />
        <Numero label="Alto de cada fila (mm)" value={el.row_h_mm} paso={0.1}
          onChange={v => fijar(conAltoDeLista({ ...el, row_h_mm: v }))} />
      </div>
      <Button size="sm" variant="ghost"
        onClick={() => fijar(conAltoDeLista({ ...el, row_h_mm: altoDeFila(el.value_style?.font_size_pt ?? el.style?.font_size_pt ?? 9) }))}>
        Ajustar el alto de las filas a la letra
      </Button>
      <CheckboxField label="Ocultar las filas vacías" checked={ocultarPorDefecto}
        description="Lo que hace cada fila, salvo que en ella se indique otra cosa."
        onChange={e => fijar({ hide_empty: e.target.checked })} />
      <CheckboxField label="Cada celda en un recuadro" checked={!!el.grid}
        onChange={e => fijar({ grid: e.target.checked ? { width_mm: 0.3 } : undefined })} />
      <EstiloCorto titulo="Rótulos" estilo={el.label_style ?? {}} onChange={label_style => fijar({ label_style })} />
      <EstiloCorto titulo="Valores" estilo={el.value_style ?? {}} onChange={value_style => fijar({ value_style })} />
    </fieldset>
  )
}

function OpcionesDeImagen({ el, fijar }: { el: Elemento; fijar: (c: Partial<Elemento>) => void }) {
  return (
    <div className="space-y-2">
      <SelectorDeArchivo kind="image" etiqueta="Imagen" value={el.asset} onChange={asset => fijar({ asset })} />
      <SelectField dense label="Ajuste" value={el.fit ?? 'contain'} onChange={e => fijar({ fit: e.target.value as Elemento['fit'] })}>
        <option value="contain">Completa, sin deformar</option>
        <option value="cover">Llenar el recuadro (recorta)</option>
        <option value="stretch">Estirar al recuadro</option>
      </SelectField>
      <Numero label="Opacidad (0 a 1)" value={el.opacity ?? 1} paso={0.05}
        onChange={v => { const o = Math.min(1, Math.max(0, v)); fijar({ opacity: o === 1 ? undefined : o }) }} />
    </div>
  )
}

function OpcionesDeBarras({ el, fijar }: { el: Elemento; fijar: (c: Partial<Elemento>) => void }) {
  const code39 = el.symbology === 'code39'
  return (
    <div className="space-y-2">
      <SelectField dense label="Tipo de código" value={el.symbology ?? 'code128'}
        onChange={e => fijar({ symbology: e.target.value as Elemento['symbology'], check_digit: undefined })}
        hint={code39 ? 'Code 39: números, mayúsculas y - . $ / + % espacio.' : 'Code 128: cualquier texto; los números quedan más compactos.'}>
        <option value="code128">Code 128</option>
        <option value="code39">Code 39</option>
      </SelectField>
      <TextField dense label="Contenido" value={el.value ?? ''} onChange={e => fijar({ value: e.target.value })}
        hint="Texto fijo o campos entre llaves: {Encabezado/IdDoc/Folio}." inputProps={{ spellCheck: false }} />
      <CheckboxField label="Texto bajo las barras" checked={el.show_text !== false} onChange={e => fijar({ show_text: e.target.checked })} />
      {code39 && (
        <CheckboxField label="Con dígito verificador (módulo 43)" checked={!!el.check_digit}
          onChange={e => fijar({ check_digit: e.target.checked })} />
      )}
    </div>
  )
}

/**
 * Sin elemento elegido, el panel muestra la hoja: el PDF de fondo (el diseño
 * con logos y recuadros sobre el que se imprimen los datos) y la ayuda.
 */
export function PropiedadesDeLaHoja({ definicion, onChange }: {
  definicion: Definicion
  onChange: (d: Definicion) => void
}) {
  const fondo = definicion.background
  return (
    <div className="space-y-4 text-sm">
      <div className="text-xs text-content-subtle space-y-2 p-1">
        <p className="font-semibold text-content">Ningún elemento elegido</p>
        <p>Hacer clic en un elemento del lienzo para editarlo, o arrastrar uno desde la paleta.</p>
        <p>Para elegir varios: Shift+clic en cada uno, o arrastrar un recuadro desde una zona vacía de la hoja (entra lo que queda completamente dentro). Ctrl+A elige todo lo de la página.</p>
        <p>Teclado: flechas para mover 1 mm (con Shift, 5 mm), Supr para borrar, Ctrl+Z y Ctrl+Y para deshacer y rehacer, Ctrl+C y Ctrl+V para copiar. Alt mientras se arrastra desactiva el imán.</p>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-1">Hoja</legend>
        <SelectorDeArchivo kind="background" etiqueta="PDF de fondo" value={fondo?.asset}
          onChange={asset => onChange({ ...definicion, background: asset ? { pages: 'first', ...fondo, asset } : undefined })} />
        <p className="text-[11px] text-content-subtle">
          El diseño de la hoja (logos, recuadros, datos fijos de la empresa) hecho en cualquier programa y exportado a
          PDF. Los datos del documento se imprimen encima.
        </p>
        {fondo && (
          <SelectField dense label="Páginas con fondo" value={fondo.pages ?? 'first'}
            onChange={e => onChange({ ...definicion, background: { ...fondo, pages: e.target.value as 'first' | 'all' } })}
            hint="Si el PDF trae 2 páginas, la segunda va en las páginas siguientes.">
            <option value="first">Solo la primera</option>
            <option value="all">Todas</option>
          </SelectField>
        )}
      </fieldset>
    </div>
  )
}

export function Propiedades({ el, datos, camposDelDetalle, camposDeReferencias, onChange, onBorrar, onDuplicar }: {
  el: Elemento | null
  datos?: DatosDelElemento
  camposDelDetalle: CampoDeLista[]
  camposDeReferencias: CampoDeLista[]
  onChange: (el: Elemento) => void
  onBorrar: () => void
  onDuplicar: () => void
}) {
  if (!el) {
    return (
      <div className="text-xs text-content-subtle space-y-2 p-1">
        <p className="font-semibold text-content">Ningún elemento elegido</p>
        <p>Hacer clic en un elemento del lienzo para editarlo, o arrastrar uno desde la paleta.</p>
        <p>Para elegir varios: Shift+clic en cada uno, o arrastrar un recuadro desde una zona vacía de la hoja (entra lo que queda completamente dentro). Ctrl+A elige todo lo de la página.</p>
        <p>Teclado: flechas para mover 1 mm (con Shift, 5 mm), Supr para borrar, Ctrl+Z y Ctrl+Y para deshacer y rehacer, Ctrl+C y Ctrl+V para copiar. Alt mientras se arrastra desactiva el imán.</p>
      </div>
    )
  }
  const fijar = (cambio: Partial<Elemento>) => onChange({ ...el, ...cambio })
  const esTabla = el.type === 'detail_table' || el.type === 'references_table'
  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-content">{NOMBRE_TIPO[el.type]}</p>
          <p className="text-[11px] font-mono text-content-subtle">{el.id}</p>
        </div>
        <div className="flex gap-1">
          <IconButton label="Duplicar" icon={<Copy size={14} />} bare onClick={onDuplicar} disabled={el.type === 'detail_table'} />
          <IconButton label="Borrar" icon={<Trash2 size={14} />} bare onClick={onBorrar} />
        </div>
      </div>
      {datos?.error && <p role="alert" className="text-xs text-danger-content-soft">{datos.error}</p>}
      {datos && !datos.visible && (
        <p className="text-xs text-content-muted">Con este ejemplo la condición no se cumple: no se imprime.</p>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Numero label="X (mm)" value={el.x_mm} paso={0.5} onChange={v => fijar({ x_mm: v })} />
        <Numero label="Ancho (mm)" value={el.w_mm} paso={0.5} onChange={v => fijar({ w_mm: v })} />
        {el.type === 'detail_table' ? (
          <>
            <Numero label="Inicio en la 1.ª página" value={el.y_mm} paso={0.5} onChange={v => fijar({ y_mm: v })} />
            <Numero label="Inicio en las siguientes" value={el.y_next_mm} paso={0.5} onChange={v => fijar({ y_next_mm: v })} />
            <Numero label="Final de la zona (mm)" value={el.bottom_mm} paso={0.5} onChange={v => fijar({ bottom_mm: v })} />
          </>
        ) : (
          <>
            <Numero label="Y (mm)" value={el.y_mm} paso={0.5} onChange={v => fijar({ y_mm: v })} />
            <Numero label="Alto (mm)" value={el.h_mm} paso={0.5} onChange={v => fijar({ h_mm: v })} />
          </>
        )}
      </div>

      <SelectField dense label="Copia" value={el.copy ?? 'both'} onChange={e => fijar({ copy: e.target.value as Elemento['copy'] })}
        hint="La cedible es la copia que se entrega al ceder la factura.">
        <option value="both">Original y cedible</option>
        <option value="original">Solo la original</option>
        <option value="cedible">Solo la cedible</option>
      </SelectField>

      {el.type !== 'detail_table' && (
        <div className="grid grid-cols-2 gap-2">
          <SelectField dense label="Páginas" value={el.pages ?? 'all'} onChange={e => fijar({ pages: e.target.value as Elemento['pages'] })}>
            <option value="all">Todas</option><option value="first">Solo la primera</option><option value="last">Solo la última</option>
          </SelectField>
          <SelectField dense label="Capa" value={el.z ?? 'front'} onChange={e => fijar({ z: e.target.value as Elemento['z'] })}>
            <option value="front">Delante</option><option value="back">Detrás del detalle</option>
          </SelectField>
        </div>
      )}

      {el.type === 'text' && (
        <TextField dense multiline rows={3} label="Contenido" value={el.text ?? ''} onChange={e => fijar({ text: e.target.value })}
          hint="Los campos van entre llaves, con formato opcional: {Encabezado/Totales/MntTotal|money}. Soltar un campo de la paleta sobre el texto lo agrega."
          inputProps={{ spellCheck: false }} />
      )}
      {el.type === 'qr' && (
        <TextField dense label="Contenido del QR" value={el.value ?? ''} onChange={e => fijar({ value: e.target.value })}
          inputProps={{ spellCheck: false }} />
      )}
      {el.type === 'timbre' && (
        <CheckboxField label="Con la leyenda de la resolución" checked={el.legend !== false}
          description="El SII exige el timbre de 2×5 a 4×9 cm; la leyenda suma 8 mm."
          onChange={e => fijar({ legend: e.target.checked })} />
      )}
      {el.type === 'field_list' && <EditorDeLista el={el} fijar={fijar} />}
      {el.type === 'image' && <OpcionesDeImagen el={el} fijar={fijar} />}
      {el.type === 'barcode' && <OpcionesDeBarras el={el} fijar={fijar} />}
      {el.type === 'detail_table' && <OpcionesDelDetalle el={el} camposDeReferencias={camposDeReferencias} fijar={fijar} />}
      {esTabla && (
        <EditorDeColumnas columnas={el.columns ?? []} onChange={columns => fijar({ columns })}
          visibles={datos?.columns_visible}
          campos={el.type === 'detail_table' ? camposDelDetalle : camposDeReferencias} />
      )}

      {el.type !== 'timbre' && el.type !== 'qr' && el.type !== 'barcode' && (
        <EditorDeEstilo estilo={el.style ?? {}} onChange={style => fijar({ style })}
          conTexto={el.type !== 'rect' && el.type !== 'line' && el.type !== 'image'} />
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-content-muted">Condición (avanzado)</summary>
        <div className="mt-2">
          <TextField dense label="Se imprime solo si" value={el.condition ?? ''} placeholder="tipo in [110, 111, 112]"
            onChange={e => fijar({ condition: e.target.value || undefined })}
            hint="Ejemplos: cedible · tipo == 33 · campo('Encabezado/Receptor/GiroRecep') != ''"
            inputProps={{ spellCheck: false }} />
        </div>
      </details>
    </div>
  )
}
